import { useCallback, useMemo } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { TableHeaderColumn, TableProps } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';

import { FormattedCompany } from 'components/company/CompanyTable/helpers';
import { URLS } from 'components/shared/App/constants';
import usePagination from 'hooks/usePagination';
import useSearchQuery from 'hooks/useSearchQuery';

import { DEFAULT_COLUMN, SEARCH_OPTIONS, TABLE_COLUMNS } from './constants';
import {
  SimplifiedCombinedEnrolment,
  simplifyCombinedEnrolment,
  sortData,
} from './helpers';
import useRateRequestTableSorting from './useRateRequestTableSorting';

import { useRateRequestListContext } from '../RateRequestListPage/context';

export interface RateRequestTablePropsHook extends TableProps {}

export default function useRateRequestTableProps(): RateRequestTablePropsHook {
  const { isLoading, combined } = useRateRequestListContext();

  const { push } = useHistory();

  const simplified = useMemo(
    () => simplifyCombinedEnrolment(combined),
    [combined],
  );

  const { hits } = useSearchQuery(simplified, 'rateRequests', SEARCH_OPTIONS);

  const onRowClicked = useCallback((row?: StringIndexedObject) => {
    if (!row) return;
    push(generatePath(URLS.company, { id: row.original.id }));
  }, []);

  const { sorting, order, onAction } = useRateRequestTableSorting();

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits?.length || 0,
  );

  const data = useMemo(() => {
    const rawData = hits.map((hit) => hit.item);
    return (
      sortData<SimplifiedCombinedEnrolment>(
        rawData || [],
        sorting,
        order,
      )?.slice(pageIndex * perPage, pageIndex * perPage + perPage) || []
    );
  }, [hits, sorting, order, pageIndex, perPage]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex],
  );

  return {
    data: data || [],
    columns: TABLE_COLUMNS as unknown as TableHeaderColumn[],
    defaultColumn: DEFAULT_COLUMN as unknown as TableHeaderColumn,
    hoverPopover: null,
    onRowClicked,
    onAction,
    pageIndex,
    totalPages,
    setPage,
    isLoading,
    isValidating: false,
  };
}
